import { Component, OnInit, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RequestService, TissueCollectionType } from '../../shared/services/request.service';
import { RequestBarcodeImage } from '../request-barcode/request-barcode.component';
import { Observable } from 'rxjs';
import { UserService, User } from '../../shared/services/user.service';
import { asEnumerable, Enumerable } from 'linq-es2015';
import { PlantCareTemplateService, PlantCareTemplate } from '../../shared/services/plant-care-template.service';
import { FacilityTypeService, FacilityType } from '../../shared/services/facility-type.service';
import { ViewableRequest, ViewableCeLogistics, ViewableRequestComment, ViewablePlanningTreatmentDetail, ViewablePlanningTreatmentComment, RequestedSampling } from '../review-request/review-request.model';
import { RequestCommentsService } from '../../shared/services/request-comments.service';
import { FacilityNameService, FacilityName } from '../../shared/services/facility-name.service';
import { PlanningService, PlanningTreatmentDetail, PlanningTreatmentComment } from '../../shared/services/planning.service';
import { LaboratoryService, Laboratory } from '../../shared/services/laboratory.service';
import { GrowthStageService, GrowthStage } from '../../shared/services/growth-stage.service';
import { TissueTypeService, TissueType } from '../../shared/services/tissue-type.service';
import { SettingsService } from 'research.web.settings';
import { Settings } from '../../settings';
import { RequestTypeService, RequestType } from '../../shared/services/request-type.service';

@Component({
  selector: 'app-review-request-page',
  templateUrl: './review-request-page.component.html',
  styleUrls: ['./review-request-page.component.css']
})
export class ReviewRequestPageComponent implements OnInit {
  private id: number;

  public request: ViewableRequest
  public barcodeImage: RequestBarcodeImage;  
  public ceLogistics: ViewableCeLogistics;
  public requestComments: ViewableRequestComment[];
  private config: ConfigurationData;
  public settings: Settings;

  public get isWheatRequest(): boolean {
    return this.request.cropId === this.settings.wheatCropId;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private requestService: RequestService,
    private userService: UserService,
    private plantCareTemplateService: PlantCareTemplateService,
    private facilityTypeService: FacilityTypeService,
    private facilityNameService: FacilityNameService,
    private requestCommentService: RequestCommentsService,
    private planningService: PlanningService,
    private labService: LaboratoryService,
    private growthStageService: GrowthStageService,
    private tissueTypeService: TissueTypeService,
    private settingsService: SettingsService,
    private requestTypeService: RequestTypeService,
  ) 
    { 
      this.settings = this.settingsService.get<Settings>("_appSettings");
    }

  ngOnInit() {
    this.id = this.activatedRoute.snapshot.params.id;
    this.request = this.activatedRoute.snapshot.data.request;
    this.barcodeImage = this.activatedRoute.snapshot.data.requestBarcode;

    this.fetchCeLogistics();
    this.fetchRequestComments();
  }

  public refreshView(event) {
    this.fetchRequest();
    this.fetchRequestStatus();
    this.fetchCeLogistics();
    this.fetchRequestComments();
  }

  private fetchRequest() {
    this.requestService
    .getDesign(this.request.id)
    .subscribe(r => {
      this.request.seedRequestDesired = r.seedRequestDesired;
    });
  }

  private fetchRequestStatus() {
    this.requestService
        .getRequestInformation(this.request.id)
        .subscribe(s => {
          this.request.requestNumber = s.requestNumber;
          this.request.statusId = s.statusId;
        });
  };

  private fetchCeLogistics(): void {
    Observable.forkJoin(
      this.requestService.getCeLogistics(this.id),
      this.planningService.getPlanningNotes(this.id),
      this.userService.getAll(),
      this.plantCareTemplateService.allPlantCareTemplates(),
      this.facilityTypeService.getAll(),
      this.facilityNameService.getAll(),
      this.labService.getAll(),
      this.growthStageService.getAll(),
      this.tissueTypeService.getAll(),
      this.requestTypeService.getAll()
    ).map(([
      ceLogistics,
      notes,
      users,
      pcts,
      facilityTypes,
      facilityNames,
      labs,
      growthStages,
      tissueTypes,
      requestTypes
    ]) => {
        const planningNotes = notes || { };
        const tl = ceLogistics.tacticalLiaisonId ? asEnumerable(<User[]>users).Single(u => u.id === ceLogistics.tacticalLiaisonId) : null;
        const pct = planningNotes.plantCareTemplateId ? asEnumerable(<PlantCareTemplate[]>pcts).Single(t => t.id === planningNotes.plantCareTemplateId) : null;
      const aoi = ceLogistics.aoiName;
      const requestType = ceLogistics.requestTypeId ? asEnumerable(<RequestType[]>requestTypes).Single(u => u.id === ceLogistics.requestTypeId) : null;

        this.config = {
          facilityTypes: asEnumerable(facilityTypes),
          facilityNames: asEnumerable(facilityNames),
          laboratories: asEnumerable(labs),
          growthStages: asEnumerable(growthStages),
          tissueTypes: asEnumerable(tissueTypes)
        };

        let samplings: RequestedSampling[] = null;
        if (planningNotes.samplingNeeds) {
          samplings = [];
          planningNotes.samplingNeeds.forEach(s => {
            let laboratory = s.laboratoryId ? this.config.laboratories.SingleOrDefault(l => l.id === s.laboratoryId) : null;
            let labName = laboratory ? laboratory.name : null;
            let assay = s.laboratoryAssayId ? asEnumerable(laboratory.assays).Single(l => l.id === s.laboratoryAssayId).name : null;
            let tissueType = s.tissueTypeId ? this.config.tissueTypes.SingleOrDefault(l => l.id === s.tissueTypeId).name : null;

            let tissueCollectionType = TissueCollectionType[s.tissueCollectionType];
            let growthStage = s.growthStageId !== null ? this.config.growthStages.SingleOrDefault(l => l.id === s.growthStageId).name : null;

            samplings.push({ 
              assay: assay,
              laboratory: labName,
              tissueType: tissueType,
              order: s.order,
              tissueCollectionType: tissueCollectionType,
              growthStage: growthStage,
              daysAfterPlanting: s.daysAfterPlanting,
              notes: s.notes
            });
          });
        }

        this.ceLogistics = <ViewableCeLogistics>{
          requestId: ceLogistics.requestId,
          tacticalLiaison: tl ? `${tl.givenName} ${tl.surname}` : null,
          tagsOnly: planningNotes.tagsOnly,
          insectsAllowed: planningNotes.insectsAllowed != null ? planningNotes.insectsAllowed : true,
          specificAoiRequired: planningNotes.specificAoiRequired,
          plannedAoiLocation: planningNotes.plannedAoiLocation,
          plantCareTemplate: pct ? pct.name : "Not Applicable",
          notes: planningNotes.notes,
          requestType: requestType ? requestType.name : null,
          plannedPlantingDate: planningNotes.plannedPlantingDate,
          facilityType: ceLogistics.facilityId ? this.config.facilityTypes.Single(t => t.id === ceLogistics.facilityId).name : null,
          prismExperimentName: ceLogistics.prismExperimentName,
          aoi: aoi,
          actualPlantDate: ceLogistics.actualPlantDate,
          requestedSamplings: samplings,
          treatmentDetails: planningNotes.treatmentDetails ? 
                              asEnumerable(<PlanningTreatmentDetail[]>planningNotes.treatmentDetails)
                                .Select(d => <ViewablePlanningTreatmentDetail>{
                                  treatmentNumber: d.treatmentNumber,
                                  flatStorageFacilityType: d.flatStorageFacilityTypeId ? this.config.facilityTypes.Single(t => t.id === d.flatStorageFacilityTypeId).name : null,
                                  flatStorageFacilityLocation: d.flatStorageFacilityLocationId ? this.config.facilityNames.Single(n => n.id === d.flatStorageFacilityLocationId).name : null,
                                  automationPotStorageFacilityType:  d.automationPotStorageFacilityTypeId ? this.config.facilityTypes.Single(t => t.id === d.automationPotStorageFacilityTypeId).name : null,
                                  automationPotStorageLocation:  d.automationPotStorageLocationId ? this.config.facilityNames.Single(n => n.id === d.automationPotStorageLocationId).name : null,
                                  staticPotStorageFacilityType: d.staticPotStorageFacilityTypeId ? this.config.facilityTypes.Single(t => t.id === d.staticPotStorageFacilityTypeId).name : null,
                                  staticPotStorageLocation: d.staticPotStorageLocationId ? this.config.facilityNames.Single(n => n.id === d.staticPotStorageLocationId).name : null,
                                  comments: d.comments ? asEnumerable(<PlanningTreatmentComment[]>d.comments)
                                                          .Select(c => <ViewablePlanningTreatmentComment>{
                                                            instructionType: c.instructionType,
                                                            instructionSubtype: c.instructionSubtype,
                                                            comment: c.comment
                                                          })
                                                          .OrderBy(c => c.instructionType)
                                                          .ThenBy(c => c.instructionSubtype)
                                                          .ToArray() : null
                                })
                                .ToArray() : null,
        };
      }).subscribe();
  }

  private fetchRequestComments(): void {
    Observable.forkJoin(
      this.requestCommentService.getAll(this.id),
      this.userService.getAll()
    ).map(
      ([
        comments, 
        users
      ]) => {
        let requestComments: ViewableRequestComment[] = [];
        for(let requestComment of comments) {
          let user = asEnumerable(users).Single(u => u.id === requestComment.createdBy);

          requestComments.push({
            createdOn: requestComment.createdOn,
            createdBy: `${user.givenName} ${user.surname}`,
            comment: requestComment.comment,
            source: requestComment.source
          })
        }

        return requestComments;

      }).subscribe((requestComments) => this.requestComments = requestComments);

  }
}

export interface ConfigurationData {
  facilityTypes: Enumerable<FacilityType>;
  facilityNames: Enumerable<FacilityName>;
  laboratories: Enumerable<Laboratory>;
  tissueTypes: Enumerable<TissueType>;
  growthStages: Enumerable<GrowthStage>;
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-2/_work/98/s/src/app/view-request/review-request-page/review-request-page.component.ts